import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import { useHistory } from 'react-router';
import Cookies from 'universal-cookie';
import {
  Box,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Menu,
  Toolbar,
  Typography,
} from '@mui/material';
import MuiAppBar from '@mui/material/AppBar';
import MuiDrawer from '@mui/material/Drawer';
// Icons
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AccountCircle from '@mui/icons-material/AccountCircle';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import HomeIcon from '@mui/icons-material/Home';
import BadgeIcon from '@mui/icons-material/Badge';
import LogoutIcon from '@mui/icons-material/Logout';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CloudIcon from '@mui/icons-material/Cloud';
import MenuBookIcon from '@mui/icons-material/MenuBook';
// Custom components
import DataContext from '../../Contexts/DataContext';
import { useAuth } from '../../Contexts/AuthContext';
import SimulationControls from '../SimulationControls/SimulationControls';
import HelpWidget from '../Knowledgebase/HelpWidget';



const drawerWidth = 240;

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    '& .MuiDrawer-paper': {
      position: 'relative',
      whiteSpace: 'nowrap',
      width: drawerWidth,
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
      boxSizing: 'border-box',
      ...(!open && {
        overflowX: 'hidden',
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
        width: theme.spacing(7),
        [theme.breakpoints.up('sm')]: {
          width: theme.spacing(9),
        },
      }),
    },
  }),
);

export default function Navbar(props){

  const history = useHistory();
  const auth = useAuth();

  const [navOpen, setNavOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const accountMenuOpen = Boolean(anchorEl);
  
  const toggleNavOpen = () => {
    setNavOpen(!navOpen);
  }
  
  const handleAccountMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleAccountMenuClose = () => {
    setAnchorEl(null);
  };

  const nextPath = (path) => {
    history.push(path);
  }

  const handleLogout = () => {
    setAnchorEl(null);
    auth.logout();
  }

  let {
    title
  } = props;

  return (
    <>
      <AppBar position='absolute' open={navOpen}>
        <Toolbar
          sx={{
            pr: '24px',
          }}
        >
          <IconButton
            edge='start'
            color='inherit'
            aria-label='open drawer'
            onClick={toggleNavOpen}
            sx={{
              marginRight: '36px',
              ...(navOpen && { display: 'none' }),
            }}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            component='h1'
            variant='h6'
            color='inherit'
            noWrap
            sx={{ flexGrow: 1 }}
          >
            {title}
          </Typography>
          
          {/* Simulation Controls - Only visible to Super Users */}
          <Box sx={{ mr: 2 }}>
            <SimulationControls />
          </Box>

          {/* Help Widget */}
          <HelpWidget />

          <IconButton
            aria-label='account of current user'
            aria-controls='menu-appbar'
            aria-haspopup='true'
            color='inherit'
            onClick={handleAccountMenuOpen}//this.nextPath('/account')}
            id='account-icon'
            size='large'
          >
            <AccountCircle />
          </IconButton>
          <Menu
            id='account-menu'
            anchorEl={anchorEl}
            open={accountMenuOpen}
            onClose={handleAccountMenuClose}
            MenuListProps={{
              'aria-labelledby': 'basic-button',
            }}
          >
            <MenuItem 
              onClick={() => {
                nextPath('/account');
                handleAccountMenuClose();
              }}
            >
              <ListItemIcon>
                <BadgeIcon />
              </ListItemIcon>
              <Typography>
                Account Dashboard
              </Typography>
            </MenuItem>
            <Divider />
            <MenuItem 
              onClick={handleLogout}
            >
              <ListItemIcon>
                <LogoutIcon />
              </ListItemIcon>
              <Typography>
                Logout  
              </Typography>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      
      <Drawer variant='permanent' open={navOpen}>
        <Toolbar
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            px: [1],
          }}
        >
          <IconButton onClick={toggleNavOpen}>
            <ChevronLeftIcon />
          </IconButton>
        </Toolbar>
        <Divider />
        <List>
          <ListItem
            button
            onClick={() => nextPath('/project-home')}
          >
            <ListItemIcon>
              <HomeIcon />
            </ListItemIcon>
            <ListItemText primary='Project Home' />
          </ListItem>
          <ListItem
            button
            onClick={() => nextPath('/dashboard')}
          >
            <ListItemIcon>
              <DashboardIcon />
            </ListItemIcon>
            <ListItemText primary='Dashboard' />
          </ListItem>
          <ListItem
            button
            onClick={() => nextPath('/new-project')}
          >
            <ListItemIcon>
              <AddCircleIcon />
            </ListItemIcon>
            <ListItemText primary='New Project' />
          </ListItem>

          <ListItem
            button
            onClick={() => nextPath('/export')}
          >
            <ListItemIcon>
              < CloudIcon/>
            </ListItemIcon>
            <ListItemText primary='Backup' />
          </ListItem>

          {/* Knowledgebase CMS — Super Users only (userType 0) */}
          {auth.activeUser?.userType === 0 && (
            <>
              <Divider />
              <ListItem
                button
                onClick={() => nextPath('/admin/knowledgebase')}
              >
                <ListItemIcon>
                  <MenuBookIcon />
                </ListItemIcon>
                <ListItemText primary='Help CMS' />
              </ListItem>
            </>
          )}
          
        </List>
        <Divider />
      </Drawer>
    </>
  );
}